import {
  AutocompleteWithLabel,
  InputWithLabel,
  SelectWithLabel
} from '@adotel/components'
import { useLazyQuery, useQuery } from '@apollo/client'
import {
  Switch,
  FormControlLabel,
  Button,
  MenuItem,
  Grid,
  CircularProgress,
  Grow,
  Typography
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { ENTITY_LIST_QUERY } from '../../../graphql/entityList'
import { WALLET_TYPES_QUERY } from '../../../graphql/walletTypes'
import { addCommaToPrice, removeCommaFromPrice } from '../../../helpers/numbers'
import { getCurrenciesAvailable } from '../../../helpers/userData'
import { getTranslate, lang } from '../../../localization'
import { EntityList } from '../../../types/entityList'
import { EntityType } from '../../../types/reserveMonitoringList'
import { WalletInput, WalletType } from '../../../types/wallet'
import useStyles from './styles/stepOneWalletDetails.styles'

interface StepOneWalletDetailsProps {
  setselectedWalletType: (walletType: WalletType) => void
  walletInput: WalletInput
  setwalletInput: React.Dispatch<React.SetStateAction<WalletInput>>
  selectedWalletType: WalletType | undefined
}

const StepOneWalletDetails: React.FC<StepOneWalletDetailsProps> = ({
  setselectedWalletType,
  setwalletInput,
  walletInput,
  selectedWalletType
}) => {
  const classes = useStyles()
  const [options, setOptions] = useState<EntityList[]>([])

  const [autoCompleteLabelForEntity, setautoCompleteLabelForEntity] =
    useState<any>(null)
  const [autoCompleteLabelForCurrency, setautoCompleteLabelForCurrency] =
    useState<any>(null)

  // Fetch wallet types from the API
  const { data, loading, error } = useQuery<{ walletTypes: WalletType[] }>(
    WALLET_TYPES_QUERY
  )

  const [getEntityList, { loading: loadingForEntityList }] = useLazyQuery<{
    entityList: EntityList[]
  }>(ENTITY_LIST_QUERY, {
    onCompleted: (res) => {
      setOptions(res.entityList)
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  useEffect(() => {
    if (walletInput.entity && walletInput.entity?.entityType) {
      getEntityList({
        variables: {
          entityType: walletInput.entity.entityType
        }
      })
    }
  }, [walletInput.entity?.entityType])

  useEffect(() => {
    setautoCompleteLabelForEntity({
      value: walletInput.entity.entityID,
      label: walletInput.entity.entityName
    })
    setautoCompleteLabelForCurrency({
      value: walletInput.currency,
      label: getTranslate(walletInput.currency || '')
    })
  }, [walletInput.currency, walletInput.entity.entityID])

  // Render loading and error states
  if (loading) return <CircularProgress />
  if (error) return <p>Error loading wallet types</p>

  return (
    <div className={classes.container}>
      <Grid container spacing={3}>
        {/* Entity Dropdown */}
        <Grid item xs={12} sm={4}>
          <SelectWithLabel
            label={getTranslate('Entity Type')}
            placeholder={getTranslate('Select the Entity Type')}
            fullWidth
            value={walletInput.entity?.entityType}
            onChange={(event: { target: { value: any } }) => {
              if (walletInput.entity) {
                setwalletInput({
                  ...walletInput,
                  entity: {
                    ...walletInput.entity,
                    entityType: event.target.value as EntityType
                  }
                })
              } else {
                setwalletInput({
                  ...walletInput,
                  entity: {
                    entityType: event.target.value as EntityType,
                    entityID: '',
                    entityCode: undefined,
                    entityName: ''
                  }
                })
              }
            }}
            size="small">
            <MenuItem
              key="Channel"
              value="Channel"
              classes={{ root: lang === 'fa' ? classes.menuItem : undefined }}>
              {getTranslate('Channel')}
            </MenuItem>
            <MenuItem
              key="Provider"
              value="Provider"
              classes={{ root: lang === 'fa' ? classes.menuItem : undefined }}>
              {getTranslate('Provider')}
            </MenuItem>
          </SelectWithLabel>
        </Grid>

        {/* Entity Type Dropdown */}
        <Grid item xs={12} sm={4}>
          <AutocompleteWithLabel
            options={loadingForEntityList && options.length > 0 ? [] : options}
            inputSize="small"
            getOptionLabel={(option) => option.label}
            value={autoCompleteLabelForEntity}
            getOptionSelected={(option, value) => option.value === value.value}
            onChange={(_, value: any) => {
              if (value && walletInput.entity) {
                // setautoCompleteLabelForEntity({
                //   value: value.id,
                //   label: value.label
                // })

                setwalletInput({
                  ...walletInput,
                  entity: {
                    ...walletInput.entity,
                    entityID: value.id,
                    entityCode: value.entityCode,
                    entityName: value.label
                  }
                })
              } else if (walletInput.entity) {
                // setautoCompleteLabelForEntity(null)
                setwalletInput({
                  ...walletInput,
                  entity: {
                    ...walletInput.entity,
                    entityID: '',
                    entityCode: undefined,
                    entityName: ''
                  }
                })
              }
            }}
            label={getTranslate('Entity')}
            loading={loadingForEntityList}
            loadingText={getTranslate('Loading') + '...'}
          />
        </Grid>

        {/* Currency Dropdown */}
        <Grid item xs={12} sm={4}>
          <AutocompleteWithLabel
            data-cy="AccommodationAccountsCurrencyFilter"
            fullWidth
            placeholder={getTranslate('select currency')}
            value={autoCompleteLabelForCurrency}
            onChange={(_, v: any) => {
              if (v) {
                setwalletInput({
                  ...walletInput,
                  currency: v.value
                })
              } else {
                setwalletInput({
                  ...walletInput,
                  currency: undefined
                })
              }
            }}
            inputSize="small"
            label={getTranslate('Currency')}
            options={getCurrenciesAvailable().map((currency) => ({
              label: getTranslate(currency as string),
              value: currency as string
            }))}
          />
        </Grid>

        {/* Active Toggle */}
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={
              <Switch
                checked={walletInput.active}
                onChange={() =>
                  setwalletInput({
                    ...walletInput,
                    active: !walletInput.active
                  })
                }
                color="primary"
              />
            }
            label={getTranslate('Active')}
          />
        </Grid>

        {/* Can Use Credit Toggle */}
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={
              <Switch
                checked={walletInput.canUseCredit}
                onChange={() =>
                  setwalletInput({
                    ...walletInput,
                    canUseCredit: !walletInput.canUseCredit
                  })
                }
                color="primary"
              />
            }
            label={getTranslate('Can use credit')}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={
              <Switch
                checked={walletInput.autoSettelmentEnabled}
                onChange={() =>
                  setwalletInput({
                    ...walletInput,
                    autoSettelmentEnabled: !walletInput.autoSettelmentEnabled
                  })
                }
                color="primary"
              />
            }
            label={getTranslate('Enable Auto Settlement')}
          />
        </Grid>

        {/* Wallet Type Selection */}
        <Grid item xs={12}>
          <Typography variant="body1">
            {getTranslate('Please choose your wallet type')}
          </Typography>
          <div className={classes.walletTypeSelection}>
            {data?.walletTypes.map((walletType: WalletType) => (
              <Button
                key={walletType.walletType}
                variant="outlined"
                onClick={() => {
                  setwalletInput({
                    ...walletInput,
                    walletType: walletType.walletType,
                    maxCredit: walletType.isBilateral
                      ? 0
                      : walletInput.maxCredit,
                    maxReservable: walletType.isBilateral
                      ? 0
                      : walletInput.maxReservable
                  })
                  setselectedWalletType(walletType)
                }}
                className={`${classes.walletTypeButton} ${
                  walletInput.walletType === walletType.walletType
                    ? classes.walletTypeButtonSelected
                    : classes.walletTypeButtonNotSelected
                }`}>
                {getTranslate(walletType.walletType)}
                {walletInput.walletType === walletType.walletType && (
                  <span className={classes.dotIndicator}></span>
                )}
              </Button>
            ))}
          </div>
        </Grid>
        <Grow
          in={
            !selectedWalletType?.isBilateral && selectedWalletType !== undefined
          }>
          <Grid item xs={12} container spacing={3}>
            <Grid item xs={12} sm={4}>
              <InputWithLabel
                label={getTranslate('Max Credit')}
                placeholder={getTranslate('Enter max Credit')}
                autoComplete="off"
                value={addCommaToPrice(walletInput.maxCredit)}
                onChange={(event) => {
                  const commaLessValue = removeCommaFromPrice(
                    event.currentTarget.value
                  )
                  setwalletInput({
                    ...walletInput,
                    maxCredit: Number(commaLessValue)
                  })
                }}
                inputSize="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <InputWithLabel
                label={getTranslate('Max Reservable')}
                placeholder={getTranslate('Enter max Reservable')}
                autoComplete="off"
                value={addCommaToPrice(walletInput.maxReservable)}
                onChange={(event) => {
                  const commaLessValue = removeCommaFromPrice(
                    event.currentTarget.value
                  )
                  setwalletInput({
                    ...walletInput,
                    maxReservable: Number(commaLessValue)
                  })
                }}
                inputSize="small"
              />
            </Grid>
          </Grid>
        </Grow>
      </Grid>
    </div>
  )
}

export default StepOneWalletDetails
